/**
 * Helper: Ajax cart
 * ------------------------------------------------------------------------------
 * An event based interface to support in communicating across components
 * that sends and retrieves responses from Ajax cart interactions.
 *
 * @namespace ajaxCart
 */
import axios from 'axios'
import * as cart from '@shopify/theme-cart'

import cssClasses from '../helpers/cssClasses'
import {
    on,
    serialize
} from './utils'

/**
 * DOM selectors.
 */
const selectors = {
    container: '[js-ajax-cart="container"]',
    addToCart: '[js-ajax-cart="addToCart"]',
    clearCart: '[js-ajax-cart="clearCart"]',
    remove: '[js-ajax-cart="remove"]',
    lineItem: '[js-ajax-cart="lineItem"]',
    form: '[js-ajax-cart="form"]',
    cartCounter: '[js-ajax-cart="cartCounter"]',
    cartDrawerCounter: '[js-ajax-cart="cart-drawer-counter"]'
}

/**
 * Export a new AjaxCart instance
 * @param {object} config - Settings to apply across all Ajax calls to the cart.
 */
export default (config) => {
    /**
     * Node selectors.
     */
    const nodeSelectors = {
        addToCart: [...document.querySelectorAll(selectors.addToCart)],
        cartCounter: document.querySelector(selectors.cartCounter),
        cartDrawerCounter: [...document.querySelectorAll(selectors.cartDrawerCounter)],
        clearCart: [...document.querySelectorAll(selectors.clearCart)],
        container: [...document.querySelectorAll(selectors.container)]
    }

    /**
     * Initialise component.
     */
    function init () {
        setListeners()
        setAddToCartEvents()
        setClearCartEvents()
        setRemoveEvents()

        cart.getState().then((state) => {
            window.Shopify.theme.cart = state
        })
    }

    /**
     * Set listeners.
     */
    function setListeners () {
        /**
         * Reinitialise Ajax cart on call.
         */
        window.Frame.EventBus.listen([
            'AjaxCart:init',
            'AjaxCart:reinit'
        ], () => {
            setRemoveEvents()
        })

        /**
         * React to any item changes in the cart.
         */
        window.Frame.EventBus.listen([
            'AjaxCart:itemAdded',
            'AjaxCart:itemRemoved',
            'AjaxCart:itemUpdated',
            'AjaxCart:cartCleared',
            'AjaxCart:freeGiftItemRemoved'
        ], (response) => {
            updateCounter()
            if (response !== undefined) {
                if (response.item_count <= 0) {
                    window.Frame.EventBus.emit('AjaxCart:cartEmpty', response)
                }
            }
        })

        /**
         * Global helper function to add items to cart and trigger
         * responses and linked interactions.
         */
        window.Frame.EventBus.listen('AjaxCart:addItem', ({
            id,
            options
        }) => {
            cart.addItem(id, options)
                .then((response) => {
                    return window.Frame.EventBus.emit('AjaxCart:itemAdded', response)
                })
                .catch((error) => error)
        })

        /**
         * Global helper function to remove items from the cart and trigger
         * responses and linked interactions.
         */
        window.Frame.EventBus.listen('AjaxCart:removeItem', (key) => {
            cart.removeItem(key)
                .then((response) => {
                    return window.Frame.EventBus.emit('AjaxCart:itemRemoved', response)
                })
                .catch((error) => error)
        })

        /**
         * Listen for event when item has been removed from the cart
         */
        window.Frame.EventBus.listen('AjaxCart:itemRemoved', (response) => {
            resetCartLineItems(response.attributes)
        })
    }

    /**
     * Set click events on buttons to post form data.
     */
    function setAddToCartEvents () {
        nodeSelectors.addToCart.forEach((element) => {
            on('click', element, (event) => handleAddToCart(event))
            on('keydown', element, (event) => handleAddToCart(event))
        })
    }

    /**
     * Handle add to cart event and set loading state.
     * @param {object} event - Click and key down event.
     */
    function handleAddToCart (event) {
        if (isKeyDownEvent(event) && !isKeyPressIsEnter(event)) {
            return
        }

        event.preventDefault()
        const target = event.currentTarget
        let formData

        const {
            currentPage,
            installation,
            productToAdd
        } = target.dataset

        if (installation === 'true' || currentPage !== productToAdd) {
            formData = getInstallationFormData(target)
        } else {
            formData = serialize(target.closest('form'))
        }

        setLoadingState(target)

        axios.post('/cart/add.js', formData)
            .then((response) => {
                setAddedState(target)
                window.Frame.EventBus.emit('AjaxCart:itemAdded', response)
                executeCallback()
                return response
            })
            .catch((error) => {
                setDefaultState()
                return error
            })
    }

    /**
     * Get installation form data.
     * @param {HTMLElement} target the clicked button.
     */
    function getInstallationFormData (target) {
        const {
            childComparePrice,
            childId,
            fittingTime,
            installation,
            installationCost,
            parentComparePrice,
            parentId,
            parts,
            productToAdd
        } = target.dataset

        /**
         * Basic add to cart data.
         */
        const formData = {
            id: (productToAdd === 'parent') ? parentId : childId,
            quantity: target.closest('form').querySelector('#Quantity').value,
            properties: {}
        }

        /**
         * Properties.
         */
        if (productToAdd === 'parent') {
            formData.properties = {
                _installation_bundle: installation, // eslint-disable-line
                _installation_cost: installationCost, // eslint-disable-line
                _installation_parts: parts // eslint-disable-line
            }

            if (parentComparePrice) {
                formData.properties._compare_at_price = parentComparePrice // eslint-disable-line
            }

            if (fittingTime) {
                formData.properties._installation_time = fittingTime // eslint-disable-line
            }
        } else if (productToAdd === 'child' && childComparePrice) {
            formData.properties._compare_at_price = childComparePrice // eslint-disable-line
        }

        formData.properties.collection = []

        document.querySelectorAll('.properties-collection').forEach((input) => {
            formData.properties.collection.push(input.value)
        })

        return formData
    }

    /**
     * Set remove item events.
     */
    function setRemoveEvents () {
        [...document.querySelectorAll(selectors.remove)].forEach((element) => {
            on('click', element, (event) => handleRemoveEvent(event))
            on('keydown', element, (event) => handleRemoveEvent(event))
        })
    }

    /**
     * Handle remove event.
     * @param {Event} event - Click/keydown event object.
     */
    function handleRemoveEvent (event) {
        event.preventDefault()

        if (isKeyDownEvent(event) && !isKeyPressIsEnter(event)) {
            return
        }

        window.Frame.EventBus.emit('AjaxCart:removingItem', event.currentTarget.closest(selectors.lineItem))
        if (window.location.href.indexOf('/cart') !== -1) {
            if (window.Shopify.theme.cart.items.length <= 1) {
                cart.clearItems()
                    .then((response) => {
                        window.Frame.EventBus.emit('AjaxCart:itemRemoved', response)
                        return response
                    })
            }
        }

        cart.removeItem(event.currentTarget.dataset.key)
            .then((response) => {
                window.Frame.EventBus.emit('AjaxCart:itemRemoved', response)
                return response
            })
            .catch((error) => {
                window.Frame.EventBus.emit('AjaxCart:itemRemoved', error)
                return error
            })
    }

    /**
     * Check if key down event.
     * @param {Event} event - Keydown event object.
     */
    function isKeyDownEvent (event) {
        return event.type === 'keydown'
    }

    /**
     * Check if the return key is pressed.
     * @param {Event} event - Key code event.
     */
    function isKeyPressIsEnter (event) {
        return event.which === 13
    }

    /**
     * Set clear cart events if enabled.
     */
    function setClearCartEvents () {
        if (!nodeSelectors.clearCart) {
            return
        }

        nodeSelectors.clearCart.forEach((element) => {
            on('click', element, (event) => clearItems(event))
        })
    }

    /**
     * Handle clear cart events.
     * @param {Event} event - Click/keydown event object.
     */
    function clearItems (event) {
        event.preventDefault()

        cart.clearItems()
            .then((response) => {
                window.Frame.EventBus.emit('AjaxCart:cartCleared', response)
                return response
            })
            .catch((error) => {
                return error
            })
    }

    /**
     * Update cart counter.
     */
    function updateCounter () {
        axios.get('/cart.js')
            .then((response) => {
                renderCartCounter(response.data)
                return response.data.item_count
            })
            .catch((error) => {
                return error
            })
    }

    /**
     * Render cart counter.
     * @param {Object} response - Cart.json data.
     */
    function renderCartCounter (response) {
        nodeSelectors.cartCounter.classList.remove(cssClasses.hidden)
        const counterText = (response.item_count === 1) ? window.theme.strings.cart.general.item : window.theme.strings.cart.general.items

        nodeSelectors.cartCounter.innerText = response.item_count

        nodeSelectors.cartDrawerCounter.forEach((item) => {
            item.classList.remove(cssClasses.hidden)
            item.innerText = `${response.item_count} ${counterText}`
        })

        if ('item_count' in response) {
            if (response.item_count <= 0) {
                nodeSelectors.cartDrawerCounter.forEach((item) => {
                    item.classList.add(cssClasses.hidden)
                })
                nodeSelectors.cartCounter.classList.add(cssClasses.hidden)
            }
        }
    }

    function resetCartLineItems (attr) {
        if (!Object.keys(attr).length) {
            return
        }

        Object.keys(attr).map((key) => {
            attr[key] = ''

            return attr[key]
        })

        axios({
            method: 'post',
            url: '/cart.js',
            data: {
                attributes: attr
            }
        })
    }

    /**
     * Set loading state.
     * @param {object} target clicked add to cart button in DOM.
     */
    function setLoadingState (target) {
        [...nodeSelectors.addToCart].forEach((element) => {
            if (target === element) {
                element.innerText = window.theme.strings.addingToCart
            }
        })
    }

    /**
     * Set the loading state to loaded, then set to default.
     * @param {object} target clicked add to cart button in DOM.
     */
    function setAddedState (target) {
        [...nodeSelectors.addToCart].forEach((element) => {
            if (target === element) {
                element.innerText = window.theme.strings.addedToCart
            }
        })

        window.setTimeout(() => {
            setDefaultState(target)
        }, 3000)
    }

    /**
     * Reset the loading states back to default.
     * @param {object} target clicked add to cart button in DOM.
     */
    function setDefaultState (target) {
        [...nodeSelectors.addToCart].forEach((element) => {
            if (target === element) {
                element.innerText = window.theme.strings.addToCart
            }
        })
    }

    /**
     * Execute callback function defined in constructor.
     */
    function executeCallback () {
        return (typeof config.callback === 'function') ? config.callback() : false
    }

    /**
     * Public interface.
     */
    return Object.freeze({
        init
    })
}



// WEBPACK FOOTER //
// ./src/resources/scripts/helpers/ajax-cart.js